import { useRef, useState, useCallback, useEffect } from 'react';
import { Folder, FolderOpen, Eye, EyeOff, Settings2, Search } from 'lucide-react';
import { DRAG_MAX, COMMIT_RATIO } from '../hooks/useBlockSwipe';
import { FEATHER_MAP } from './featherIcons';
import { searchNodes } from '../services/api';

/**
 * Render a node icon — supports Feather SVGs (prefixed "feather:") and emoji text.
 *
 * @param {string} icon - Raw icon string: emoji, "feather:name", or empty
 * @param {string} [className='v2-tree-icon'] - CSS class for the wrapper span
 * @returns {JSX.Element}
 */
function renderIcon(icon, className = 'v2-tree-icon') {
  if (icon && icon.startsWith('feather:')) {
    const name = icon.slice(8);
    const IconComponent = FEATHER_MAP[name];
    if (IconComponent) {
      return (
        <span className={className}>
          <IconComponent size={14} strokeWidth={2} />
        </span>
      );
    }
  }
  return <span className={className}>{icon}</span>;
}

/**
 * V2 Left Panel — Notes panel or Settings panel.
 *
 * Props:
 *  - leftWidth           : number — panel width in px (64-260)
 *  - dragCommit          : boolean — drag crossed commit threshold (left-drag only)
 *  - onToggle            : () => void
 *  - treeData            : {name, type}[] — root-level entries
 *  - treeLoading         : boolean
 *  - treeExpanded        : {[folderPath]: children[]}
 *  - onToggleFolder      : (folderPath) => void
 *  - selectedNode        : {path, type} | null
 *  - onNodeSelect        : (path, type) => void — click opens file content on right
 *  - onNodeSettings      : (path, name, type) => void — gear opens unified settings panel
 *  - onQuickAccessFolder : (path) => void — quick-access folder click (expand in tree)
 *  - onSearchReveal      : (path) => void — search-result click (reveal + highlight, no open)
 *  - focusPath           : string | null — path to highlight in the tree (search reveal)
 *  - openDataSourceSetup : () => void
 *  - moveMode            : boolean — nodes become drop targets
 *  - onDropTargetClick   : (path, event) => void — file click during move mode
 *  - onMakeNewNodeAtPath : (path) => void — "New node..." during move mode
 *  - notesRoot           : string — notes root directory path ("" = repo root)
 *  - nodeIcons           : Object<string,string> — flat path → icon
 *  - nodeDefaults        : {file_icon, dir_icon}
 *  - quickAccessNodes    : Array<{path, name, icon, type}>
 *  - nodeTypes           : Object<string,string> — flat path → type
 *  - onAddNode           : (targetPath: string) => void — called when + button clicked
 *  - onInboxClick        : () => void — 🟢 Inbox button
 *  - onTimelineClick     : () => void — 🕒 Timeline button
 *  - isInboxActive       : boolean — true when inbox is the active selection
 */
export default function NodeV2LeftPanel({
  leftWidth,
  dragCommit,
  onToggle,
  treeData,
  treeLoading,
  treeExpanded,
  onToggleFolder,
  selectedNode,
  onNodeSelect,
  onNodeSettings,
  focusPath,
  moveMode,
  onDropTargetClick,
  onMakeNewNodeAtPath,
  notesRoot,
  nodeIcons,
  nodeDefaults,
  quickAccessNodes,
  onAddNode,
  loadingFolders,
  onSwipeProgress,
  onSwipeCancel,
  swipeEnabled = true,
  onToggleSwipe,
  onOpenSettings,
  onClearBlocks,
  onInboxClick,
  onTimelineClick,
  isInboxActive = false,
  isTimelineActive = false,
  onQuickDropTargetClick,
  onQuickAccessFolder,
  onSearchReveal,
  savingPaths,
  conflictPaths,
  timelineStatus,
  onConflictsClick,
  isConflictsActive = false,
  conflictCount = 0,
  hiddenPaths,
  focusedFolder = '',
  showAll = false,
  onToggleShowAll,
}) {
  const collapsed = leftWidth <= 74; // 64 + 10px hysteresis

  // ── Search state (folder/node name search) ──────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchActive = searchQuery.trim().length > 0;

  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await searchNodes(q);
        if (!cancelled) setSearchResults(data.results || []);
      } catch (err) {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // ── Swipe-right detection for node options ──────────────────
  const SWIPE_THRESHOLD = 10;
  const [swipeProgress, setSwipeProgress] = useState(null); // { path, ratio } | null
  const touchRef = useRef({ startX: 0, startY: 0, locked: false, path: null, name: null, deltaX: 0 });
  const mouseActive = useRef(false);
  const swipeJustCommitted = useRef(false);

  // ── Shared drag-move logic ──────────────────────────────────
  const handleDragMove = useCallback((clientX, clientY) => {
    if (moveMode) return;
    const t = touchRef.current;
    if (!t.path) return;
    const dx = clientX - t.startX;
    const dy = Math.abs(clientY - t.startY);

    // Lock direction: if vertical movement dominates, let it scroll
    if (!t.locked && Math.abs(dx) < 8 && dy < 8) return;
    if (!t.locked) {
      if (dy > Math.abs(dx)) { t.locked = true; return; } // vertical scroll
      t.locked = true;
    }
    if (dy > Math.abs(dx)) return; // vertical

    // Only right-swipe for node options
    if (dx < 0) return;

    // Clamp delta to DRAG_MAX + overshoot
    const maxDrag = DRAG_MAX + 20;
    t.deltaX = Math.min(maxDrag, dx);

    // Report progress to parent — cap visual ratio so the edge strip
    // locks at the commit threshold instead of growing indefinitely.
    const rawRatio = Math.min(1, t.deltaX / DRAG_MAX);
    const visualRatio = Math.min(rawRatio, COMMIT_RATIO);
    if (onSwipeProgress) {
      onSwipeProgress('right', rawRatio);
    }

    // Update visual edge-strip state for the swiped node
    setSwipeProgress({ path: t.path, ratio: visualRatio });
  }, [moveMode, onSwipeProgress]);

  // ── Shared drag-end logic ───────────────────────────────────
  const handleDragEnd = useCallback(() => {
    if (moveMode) return;
    const t = touchRef.current;
    if (!t.path) return;

    const absDx = Math.abs(t.deltaX);

    if (absDx > SWIPE_THRESHOLD) {
      setSwipeProgress(null);
      const ratio = Math.min(1, absDx / DRAG_MAX);
      if (ratio >= COMMIT_RATIO && onNodeSettings) {
        swipeJustCommitted.current = true;
        onNodeSettings(t.path, t.name, 'file');
      } else {
        onSwipeCancel?.();
      }
      touchRef.current = { startX: 0, startY: 0, locked: false, path: null, name: null, deltaX: 0 };
      return;
    }

    // Not enough movement — snap closed
    setSwipeProgress(null);
    onSwipeCancel?.();
    touchRef.current = { startX: 0, startY: 0, locked: false, path: null, name: null, deltaX: 0 };
  }, [moveMode, onNodeSettings, onSwipeCancel]);

  // ── Touch handlers ──────────────────────────────────────────
  const handleNodeTouchStart = (e, path, name) => {
    if (moveMode) return;
    const touch = e.touches[0];
    touchRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      locked: false,
      path,
      name,
      deltaX: 0,
    };
  };

  const handleNodeTouchMove = (e) => {
    if (moveMode) return;
    const touch = e.touches[0];
    handleDragMove(touch.clientX, touch.clientY);
  };

  const handleNodeTouchEnd = (e) => {
    handleDragEnd();
  };

  // ── Mouse handlers (desktop drag) ───────────────────────────
  const handleNodeMouseDown = (e, path, name) => {
    if (moveMode) return;
    // Only handle primary (left) button
    if (e.button !== 0) return;
    e.preventDefault(); // prevent text selection during drag

    mouseActive.current = true;
    touchRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      locked: false,
      path,
      name,
      deltaX: 0,
    };
  };

  // Global mouse-move / mouse-up listeners — attached once
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!mouseActive.current) return;
      if (!touchRef.current.path) return;
      handleDragMove(e.clientX, e.clientY);
    };

    const onMouseUp = (e) => {
      if (!mouseActive.current) return;
      mouseActive.current = false;
      if (!touchRef.current.path) return;
      handleDragEnd();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [handleDragMove, handleDragEnd]);

  // ── Quick-access path set — used for underline styling in tree ──
  const quickAccessPaths = useRef(new Set());
  quickAccessPaths.current = new Set((quickAccessNodes || []).map(qa => qa.path));

  // ── Inbox entry — find and separate from treeData for pinned rendering ──
  const inboxEntry = (treeData || []).find(item => item.name === 'inbox.md' && item.type === 'blob');
  const inboxPath = inboxEntry
    ? (inboxEntry.path || (notesRoot ? `${notesRoot}/inbox.md` : 'inbox.md'))
    : (notesRoot ? `${notesRoot}/inbox.md` : 'inbox.md');
  const regularTreeData = (treeData || []).filter(item => item.name !== 'inbox.md' || item.type !== 'blob');

  // ── Icon resolution helper ──────────────────────────────────
  // Priority: custom icon > default > hardcoded
  const resolveFileIcon = (path) => {
    if (nodeIcons?.[path]) return nodeIcons[path];
    return nodeDefaults?.file_icon || '\u{1F4C4}';
  };

  // Folder icon — per-folder custom icon first, then `_defaults` dir_icon,
  // then the default SVG (Lucide) folder, open/closed by expand state.
  const resolveFolderIcon = (path, expanded) => {
    const custom = nodeIcons?.[path];
    if (custom) return renderIcon(custom, 'v2-tree-icon');
    const dirIcon = nodeDefaults?.dir_icon;
    if (dirIcon && dirIcon.startsWith('feather:')) {
      return renderIcon(dirIcon, 'v2-tree-icon');
    }
    return (
      <span className="v2-tree-icon">
        {expanded
          ? <FolderOpen size={14} strokeWidth={2} />
          : <Folder size={14} strokeWidth={2} />}
      </span>
    );
  };

  // ── Status dot near the icon ─────────────────────────────
  // Conflict (warning color) takes precedence over uploading/saving (accent).
  const renderStatusDot = (path) => {
    if (conflictPaths?.has(path)) {
      return <span className="v2-saving-dot v2-saving-dot--conflict" title="Conflict" />;
    }
    if (savingPaths?.has(path)) {
      return <span className="v2-saving-dot" title="Uploading / saving…" />;
    }
    return null;
  };

  // ── Hidden-path check (search results) ──────────────────
  // A search result is hidden if its own path is hidden, or it lives under a
  // hidden folder.  Search must not surface hidden repos/files.
  const isPathHidden = (path) => {
    if (!path || !hiddenPaths || hiddenPaths.size === 0) return false;
    if (hiddenPaths.has(path)) return true;
    const parts = path.split('/');
    for (let i = 1; i < parts.length; i++) {
      if (hiddenPaths.has(parts.slice(0, i).join('/'))) return true;
    }
    return false;
  };
  const visibleSearchResults = searchResults.filter((r) => !isPathHidden(r.path));

  // ── Render tree nodes recursively (one level) ────────────
  const renderTreeNode = (item, depth = 0) => {
    const path = item.path || item.name;
    const isDir = item.type === 'tree';
    const isFile = item.type === 'blob';
    const isSelected = selectedNode && selectedNode.path === path;
    const isFocused = focusPath && focusPath === path;
    const isFocusFolder = isDir && focusedFolder === path;
    const hasChildren = isDir && treeExpanded[path];

    // Hidden node (file or folder) — skip entirely unless "show all" is on.
    const isHidden = hiddenPaths?.has(path);
    if (isHidden && !showAll) return null;

    // During move mode, files & dirs are drop targets (dirs expand on click)
    const dropClass = (moveMode) ? ' drop-target' : '';
    const hiddenClass = (isHidden && showAll) ? ' hidden' : '';

    return (
      <div key={path + depth}>
        <div
          className={`v2-tree-node${isSelected ? ' active' : ''}${isFocused ? ' focused' : ''}${isFocusFolder ? ' focus-folder' : ''}${dropClass}${hiddenClass}`}
          onClick={(e) => {
            // Suppress click after a swipe — the swipe already fired onNodeSettings
            if (swipeJustCommitted.current) {
              swipeJustCommitted.current = false;
              return;
            }
            if (isDir) {
              onToggleFolder(path);
            } else if (isFile) {
              if (moveMode && onDropTargetClick) {
                onDropTargetClick(path, e);
              } else {
                onNodeSelect(path);
              }
            }
          }}
          onTouchStart={(e) => { if (isFile) handleNodeTouchStart(e, path, item.name); }}
          onTouchMove={(e) => { if (isFile) handleNodeTouchMove(e); }}
          onTouchEnd={(e) => { if (isFile) handleNodeTouchEnd(e); }}
          onMouseDown={(e) => { if (isFile) handleNodeMouseDown(e, path, item.name); }}
        >
          {/* ── Swipe progress edge strip (right side only) ── */}
          {(() => {
            const strip = (swipeProgress && swipeProgress.path === path) ? swipeProgress : null;
            if (!strip) return null;
            const stripWidth = Math.round(strip.ratio * DRAG_MAX);
            const stripCommitted = strip.ratio >= COMMIT_RATIO;
            const stripBg = stripCommitted
              ? 'rgba(var(--accent-rgb), 0.25)'
              : 'rgba(128, 128, 128, 0.15)';
            return (
              <div
                className="v2-swipe-strip"
                data-side="right"
                style={{
                  right: 0,
                  width: stripWidth,
                  background: stripBg,
                }}
              />
            );
          })()}
          {isDir ? (
            <span
              className={`v2-tree-expander${hasChildren ? ' expanded' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleFolder(path);
              }}
            >
              &#x25B6;
            </span>
          ) : (
            <span className="v2-tree-expander v2-tree-expander--spacer" aria-hidden="true" />
          )}
          {isDir
            ? resolveFolderIcon(path, hasChildren)
            : (
              <span className="v2-tree-icon-wrap">
                {renderIcon(resolveFileIcon(path), 'v2-tree-icon')}
                {renderStatusDot(path)}
              </span>
            )}
          <span className="v2-tree-name" style={isFile && quickAccessPaths.current.has(path) ? { textDecoration: 'underline' } : undefined}>{item.name}</span>
          {/* Hidden badge — shown only in "show all" mode */}
          {isHidden && showAll && (
            <span className="v2-tree-hidden-badge" title="Hidden">
              <EyeOff size={12} strokeWidth={2} />
            </span>
          )}
          {/* Folder loading spinner — rendered before the gear so the gear stays
              pinned to the far right.  A second `margin-left: auto` here would
              split the row's free space with the gear and shift it while loading. */}
          {isDir && !hasChildren && loadingFolders?.has(path) && (
            <span className="v2-tree-more" style={{ padding: 0, flexShrink: 0 }}>
              <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
            </span>
          )}
          {/* Settings button (files & folders) — hidden during move mode */}
          {!moveMode && (
            <button
              className="v2-tree-options-btn"
              onClick={(e) => {
                e.stopPropagation();
                onNodeSettings(path, item.name, isDir ? 'folder' : 'file');
              }}
              title="Settings"
            >
              <Settings2 size={13} strokeWidth={2} />
            </button>
          )}
        </div>

        {/* Expanded folder children */}
        {hasChildren && (
          <div className="v2-tree-children">
            {treeExpanded[path].map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <aside
      className={`v2-left${collapsed ? ' collapsed' : ''}${dragCommit ? ' drag-commit' : ''}${moveMode ? ' move-mode' : ''}`}
      style={{ width: leftWidth, minWidth: leftWidth, maxWidth: leftWidth }}
    >
      {/* ── Expand section — full panel content ────────── */}
      <div className="v2-left-expand-section">
        {/* Header */}
        <div className="v2-left-header">
          {!moveMode && (
            <div className="v2-left-search">
              <Search size={14} strokeWidth={2} className="v2-left-search-icon" />
              <input
                className="v2-left-search-input"
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="v2-left-search-clear"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}
          {!moveMode && (
            <div className="v2-left-header-actions">
              {onToggleShowAll && (
                <button
                  className={`v2-add-node-btn v2-show-all-btn${showAll ? ' active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); onToggleShowAll(); }}
                  title={showAll ? 'Hide hidden items' : 'Show hidden items'}
                >
                  {showAll ? <Eye size={15} strokeWidth={2} /> : <EyeOff size={15} strokeWidth={2} />}
                </button>
              )}
              <button
                className="v2-add-node-btn"
                onClick={(e) => { e.stopPropagation(); if (onAddNode) onAddNode(focusedFolder || notesRoot || ''); }}
                title={focusedFolder ? `Add node in ${focusedFolder}` : 'Add node'}
              >
                +
              </button>
            </div>
          )}
        </div>

        {/* ── Inbox — pinned, clickable, fixed above the scrolling tree ── */}
        <div className="v2-tree-pinned">
          <div
            className={`v2-tree-node${isInboxActive ? ' active' : ''}${moveMode ? ' drop-target' : ''}`}
            onClick={(e) => {
              if (moveMode && onDropTargetClick) {
                onDropTargetClick(inboxPath, e);
              } else if (onInboxClick) {
                onInboxClick();
              }
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (moveMode && onDropTargetClick) {
                  onDropTargetClick(inboxPath, e);
                } else if (onInboxClick) {
                  onInboxClick();
                }
              }
            }}
          >
            <span className="v2-tree-icon-wrap">
              <span className="v2-tree-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                  <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                </svg>
              </span>
              {renderStatusDot(inboxPath)}
            </span>
            <span className="v2-tree-name">Inbox</span>
          </div>

          {/* Divider after pinned inbox */}
          <div style={{ height: 1, background: 'var(--rule)', margin: 'var(--space-xs) 0' }} />

          {/* ── Conflicts — pinned entry with a count badge (only when conflicts exist) ── */}
          {onConflictsClick && conflictCount > 0 && (
            <div
              className={`v2-tree-node${isConflictsActive ? ' active' : ''}`}
              onClick={(e) => { e.stopPropagation(); onConflictsClick(); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onConflictsClick();
                }
              }}
            >
              <span className="v2-tree-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </span>
              <span className="v2-tree-name">Conflicts</span>
              {conflictCount > 0 && (
                <span className="v2-conflict-badge">{conflictCount}</span>
              )}
            </div>
          )}
        </div>

        {/* Tree */}
        <div className="v2-tree-section">
          {/* Move-mode hint */}
          {moveMode && (
            <div style={{ padding: '0 0 var(--space-sm) 0', fontSize: 'var(--text-xs)', color: 'var(--accent)', fontStyle: 'italic' }}>
              Select a target to move the block
            </div>
          )}

          {/* "New node..." — only during move mode */}
          {moveMode && (
            <div
              className="v2-tree-node drop-target"
              onClick={(e) => {
                e.stopPropagation();
                if (onMakeNewNodeAtPath) onMakeNewNodeAtPath(notesRoot || '');
              }}
              style={{ cursor: 'pointer', margin: '0 0 var(--space-xs) 0' }}
            >
              <span className="v2-tree-icon">✨</span>
              <span className="v2-tree-name" style={{ fontStyle: 'italic', color: 'var(--accent)' }}>New node...</span>
            </div>
          )}

          {searchActive && !moveMode ? (
            <div className="v2-search-results">
              {searchLoading && <div className="v2-tree-loading">Searching…</div>}
              {!searchLoading && visibleSearchResults.length === 0 && (
                <div className="v2-tree-empty">No matches</div>
              )}
              {visibleSearchResults.map(r => (
                <div
                  key={r.path}
                  className="v2-tree-node v2-search-result"
                  onClick={() => {
                    if (onSearchReveal) onSearchReveal(r.path);
                    setSearchQuery('');
                  }}
                >
                  <span className="v2-tree-expander v2-tree-expander--spacer" aria-hidden="true" />
                  {r.type === 'tree'
                    ? resolveFolderIcon(r.path, false)
                    : renderIcon(resolveFileIcon(r.path), 'v2-tree-icon')}
                  <span className="v2-tree-name">{r.name}</span>
                  <span className="v2-search-path">{r.path}</span>
                </div>
              ))}
            </div>
          ) : treeLoading ? (
            <div className="v2-tree-loading">Loading...</div>
          ) : regularTreeData.length === 0 ? (
            <div className="v2-tree-empty">No .md files found</div>
          ) : (
            regularTreeData.map(item => renderTreeNode(item))
          )}
        </div>
      </div>

      {/* ── Collapse section — thin strip when collapsed ── */}
      <div className="v2-left-collapse-section">
        {/* Inbox + Expand + Timeline — fixed at top */}
        <div className="v2-collapsed-top">
          {onInboxClick && (
            <button
              className={`v2-collapsed-inbox-btn${isInboxActive ? ' active' : ''}`}
              onClick={(e) => { e.stopPropagation(); onInboxClick(); }}
              title="Inbox"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
              </svg>
              {renderStatusDot(inboxPath)}
            </button>
          )}
          <button
            className="v2-left-expand-btn"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            title="Expand panel"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
          </button>
          {onTimelineClick && (
            <button
              className={`v2-collapsed-timeline-btn${isTimelineActive ? ' active' : ''}`}
              onClick={(e) => { e.stopPropagation(); onTimelineClick(); }}
              title="Timeline"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              {timelineStatus && (
                <span
                  className={`v2-saving-dot${timelineStatus === 'conflict' ? ' v2-saving-dot--conflict' : ''}`}
                  title={timelineStatus === 'conflict' ? 'Conflict' : 'Uploading / saving…'}
                />
              )}
            </button>
          )}
          {onConflictsClick && conflictCount > 0 && (
            <button
              className={`v2-collapsed-conflict-btn${isConflictsActive ? ' active' : ''}`}
              onClick={(e) => { e.stopPropagation(); onConflictsClick(); }}
              title="Conflicts"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              {conflictCount > 0 && (
                <span className="v2-conflict-badge v2-conflict-badge--collapsed">{conflictCount}</span>
              )}
            </button>
          )}
        </div>

        {/* Quick access node icons — scrollable middle section */}
        <div className="v2-collapsed-qa-scroll">
          {(quickAccessNodes || []).map(qa => {
            const isActive = !isInboxActive && selectedNode && selectedNode.path === qa.path;
            return (
              <button
                key={qa.path}
                className={`v2-qa-item${isActive ? ' active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (moveMode && onQuickDropTargetClick) {
                    onQuickDropTargetClick(qa.path, e);
                  } else if (!moveMode) {
                    if (qa.type === 'folder' && onQuickAccessFolder) {
                      onQuickAccessFolder(qa.path);
                    } else if (onNodeSelect) {
                      onNodeSelect(qa.path, qa.type);
                    }
                  }
                }}
                title={qa.name}
              >
                <span className="v2-qa-icon-wrap">
                  {renderIcon(qa.icon, 'v2-qa-icon')}
                  {renderStatusDot(qa.path)}
                </span>
                <span className="v2-qa-label">{qa.name}</span>
              </button>
            );
          })}
        </div>

        {/* Action buttons — fixed at bottom */}
        <div className="v2-collapsed-bottom">
          {onToggleSwipe && (
            <button
              className={`v2-collapsed-action-btn v2-reorder-btn${swipeEnabled ? ' active' : ''}`}
              onClick={(e) => { e.stopPropagation(); onToggleSwipe(); }}
              title={swipeEnabled ? 'Reorder ON — tap to disable' : 'Reorder OFF — tap to enable'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
          )}
          {onClearBlocks && (
            <button
              className="v2-collapsed-action-btn"
              onClick={(e) => { e.stopPropagation(); onClearBlocks(); }}
              title="Clear all blocks"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21" />
                <path d="M22 21H7" />
                <path d="m5 11 9 9" />
              </svg>
            </button>
          )}
          {onOpenSettings && (
            <button
              className="v2-collapsed-action-btn"
              onClick={(e) => { e.stopPropagation(); onOpenSettings(); }}
              title="Settings"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
